import { useEffect, type ReactNode } from 'react';

/**
 * Bottom sheet — the app's one overlay pattern (design: Component · Sheet and Form).
 * Two heights: `peek` (content-sized, for a detail card or a confirm) and `tall` (90vh,
 * for forms). Enters over 240ms from the bottom; the scrim and the grabber dismiss.
 */
export interface SheetProps {
  open: boolean;
  onClose: () => void;
  height?: 'peek' | 'tall';
  label: string;
  testId?: string;
  children: ReactNode;
}

export function Sheet({
  open,
  onClose,
  height = 'peek',
  label,
  testId,
  children,
}: SheetProps): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-inverse/40 transition-opacity duration-200 ease-app"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-testid={testId}
        className={`relative flex w-full max-w-lg flex-col rounded-t-lg border border-line bg-surface pb-[env(safe-area-inset-bottom)] shadow-2xl duration-[240ms] ease-app animate-in ${
          height === 'tall'
            ? 'h-[90dvh] max-h-[calc(100dvh-env(safe-area-inset-top)-8px)]'
            : 'max-h-[calc(85dvh-env(safe-area-inset-top))]'
        }`}
        style={{ animation: 'awwyeah-sheet-in 240ms var(--ease)' }}
      >
        <div className="flex justify-center pt-2">
          <span className="h-1 w-10 rounded-sm bg-fg-3/40" />
        </div>
        {children}
      </div>
      <style>{`@keyframes awwyeah-sheet-in { from { transform: translateY(16px); opacity: .6 } to { transform: none; opacity: 1 } }`}</style>
    </div>
  );
}
